/* ==========================================================================
   RING STUDIO — Setting → Diamond → Complete

   State survives reload (localStorage) and is shareable (URL), because people
   compare settings in other tabs, go and measure a finger, and come back. A
   builder that forgets the stone you picked ten minutes ago loses the sale.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-ring-studio]');
  if (!root) return;

  var KEY = 'claviss.ring';
  var settings = [];
  var state = {
    step: 1, setting: null, stone: null,
    // No default size. A ring quietly made to a size nobody chose is the
    // worst thing this page could do, so it must be answered — with an
    // explicit "Not sure" as the easy way out.
    size: '', region: 'US',
    // The stone shape chosen ON the setting (the reference builder's model):
    // it is what step 2 is filtered to, and what the setting's photo shows.
    shape: null,
    metal: null,          // chosen metal VARIANT name, e.g. "18k Rose Gold"
    // Multi-select, like the reference builders' ?styles= plural param — a
    // buyer comparing halo against hidden halo should not have to pick one.
    styles: [], metals: [], shapes: [],
    priceMin: null, priceMax: null,
    sort: 'price-asc',
    path: 'setting'            // 'setting' = start with a setting, 'stone' = start with a diamond
  };

  /* Optional scope, defaulting to the section root -- so a call that passes a
     container is actually scoped to it rather than silently ignoring the
     argument and searching the whole section. */
  function $(s, el) { return (el || root).querySelector(s); }
  function $$(s, el) { return Array.prototype.slice.call((el || root).querySelectorAll(s)); }
  function money(n) { return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  /* Shape names come from the feed, so "a"/"an" has to be worked out rather
     than written into a sentence. Inlining this went wrong three times in three
     different strings before it was pulled out here. */
  function withArticle(word) {
    var w = String(word).toLowerCase();
    return (/^[aeiou]/.test(w) ? 'an ' : 'a ') + w;
  }
  /* Rounding is fine for a running total but not for the price a visitor is
     being asked to accept — $529.99 must not read as $530 on the one screen
     where it is the commitment. */
  function exactMoney(n) {
    return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* ---------- persistence ---------- */
  function save() {
    try {
      localStorage.setItem(KEY, JSON.stringify({
        setting: state.setting ? state.setting.id : null,
        stone: state.stone ? (state.stone.stone_no || state.stone.id) : null,
        shape: state.shape,
        metal: state.metal,
        size: state.size,
        region: state.region
      }));
    } catch (e) { /* private browsing — the builder still works, just not across reloads */ }

    try {
      var p = new URLSearchParams(location.search);
      if (state.setting) p.set('setting', state.setting.id); else p.delete('setting');
      if (state.stone) p.set('stone', state.stone.stone_no || state.stone.id); else p.delete('stone');
      if (state.shape) p.set('shape', state.shape); else p.delete('shape');
      var q = p.toString();
      history.replaceState(null, '', q ? '?' + q : location.pathname);
    } catch (e) { /* data: origins reject replaceState */ }
  }

  function restore() {
    var saved = {};
    try { saved = JSON.parse(localStorage.getItem(KEY) || '{}'); } catch (e) { saved = {}; }
    var p = new URLSearchParams(location.search);
    // A shared link wins over whatever this browser last had.
    return {
      setting: +(p.get('setting') || saved.setting) || null,
      // Kept as a string: deep links carry the vendor's stone_no, which is not
      // the same as the index row id.
      stone: String(p.get('stone') || saved.stone || ''),
      shape: p.get('shape') || saved.shape || null,
      metal: saved.metal || null,
      size: saved.size || '',
      region: saved.region || 'US'
    };
  }

  /* ---------- setting imagery ----------
     A setting is photographed once per centre-stone shape — the DSR1 renders
     are one design shot as Round, Oval, Pear, Cushion, Radiant, Emerald and
     Heart. Showing the render that matches the stone the visitor actually
     picked is the difference between a catalogue and a preview of THEIR ring,
     and it costs nothing but choosing the right file.

     Order of preference: the render for this shape, then the setting's own
     default photo, then the inline SVG illustration. Every setting works at
     whichever level it has assets for, so photography can land one shape or
     one setting at a time rather than all-or-nothing. */
  function settingImage(s, shape) {
    if (s.shape_images && shape && s.shape_images[shape]) return s.shape_images[shape];
    if (s.image) return s.image;
    return null;
  }

  function settingMedia(s, shape) {
    var src = settingImage(s, shape);
    return src
      ? '<img src="' + esc(src) + '" alt="' + esc(s.title) + '" loading="lazy">'
      : ringIcon(s.style_slug);
  }

  /* ---------- icons ---------- */
  function ringIcon(slug) {
    var t = document.getElementById('rs-ring-' + slug);
    return t ? t.innerHTML : (document.getElementById('rs-ring-solitaire') || { innerHTML: '' }).innerHTML;
  }
  function shapeIcon(shape) {
    var t = document.getElementById('ds-shape-' + String(shape).toLowerCase().replace(/\s+/g, '-'));
    return t ? t.innerHTML : '';
  }

  /* ---------- steps ---------- */
  function go(step) {
    /* Only the review needs both halves. Step 2 used to bounce back to step 1
       unless the visitor had opted into the stone-first path; now either half
       can be chosen first, so it stands on its own. */
    if (step === 3 && (!state.setting || !state.stone)) step = state.setting ? 2 : 1;
    state.step = step;
    // Entering step 2 with a setting chosen: narrow the search to stones that
    // head actually holds, so the copy "only stones that fit" stays true.
    if (step === 2 && state.setting) {
      var search = document.querySelector('[data-diamond-search]');
      if (search) {
        search.dispatchEvent(new CustomEvent('diamond:constrain', {
          detail: {
            /* The shape picked on the setting page wins. Only if none was
               picked (the stone-first path) does the list widen to everything
               the head can hold. */
            shapes: state.shape ? [state.shape]
              : state.setting.fits_shapes === 'all' ? [] : state.setting.fits_shapes.split(','),
            caratMin: state.setting.carat_min,
            caratMax: state.setting.carat_max
          }
        }));
      }
    }
    paintSteps();
    $$('[data-panel]').forEach(function (p) {
      p.setAttribute('data-active', String(+p.getAttribute('data-panel') === step));
    });
    paintContext();
    paintSource();              // the "to set in" line follows the chosen setting
    if (step === 3) paintReview();
    paintBar();                 // the forward CTA depends on which step we are on
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /* The rail order follows whichever half the visitor started with — the
     reference builder does the same via its isSWS flag, so starting from a
     diamond makes "Choose a Diamond" step 1. Panels keep fixed numbers (1
     settings, 2 diamonds, 3 review); only the RAIL reorders. */
  var PANEL = { setting: 1, stone: 2, review: 3 };

  function railOrder() {
    return state.path === 'stone'
      ? ['stone', 'setting', 'review']
      : ['setting', 'stone', 'review'];
  }

  function paintSteps() {
    var order = railOrder();
    var inner = $('.rs-steps__inner');
    if (inner) {
      var doneCount = (state.setting ? 1 : 0) + (state.stone ? 1 : 0);
      inner.style.setProperty('--rs-progress', ((doneCount + 1) / 3 * 100) + '%');
    }

    $$('[data-step]').forEach(function (b) {
      var key = b.getAttribute('data-step');
      var pos = order.indexOf(key);
      b.style.order = pos;

      var panel = PANEL[key];
      b.setAttribute('aria-current', String(panel === state.step));

      var chosen = key === 'setting' ? state.setting : key === 'stone' ? state.stone : null;
      b.setAttribute('data-done', String(!!chosen));

      /* Only Review needs both halves. Setting and Diamond are always open, so
         either can be the starting point. */
      var goBtn = b.querySelector('[data-step-go]');
      if (goBtn) goBtn.disabled = key === 'review' && !(state.setting && state.stone);

      /* Remove appears on a step only once it holds something. */
      var rm = b.querySelector('[data-step-remove]');
      if (rm) rm.hidden = !chosen;

      var n = b.querySelector('[data-n]');
      if (n) { n.textContent = pos + 1; n.hidden = !!chosen; }

      /* A finished step shows what was chosen instead of its numeral — the
         detail that makes the rail read as progress rather than as tabs. */
      var thumb = b.querySelector('[data-thumb]');
      if (thumb) {
        thumb.hidden = !chosen;
        if (chosen) {
          var src = key === 'setting'
            ? settingImage(state.setting, state.shape)
            : (state.stone.image || null);
          thumb.innerHTML = (src ? '<img src="' + esc(src) + '" alt="">' : '') +
            '<i class="rs-step__tick" aria-hidden="true">&#10003;</i>';
        }
      }

      var label = b.querySelector('.rs-step__t b [data-label]');
      if (!label) return;
      if (key === 'setting') label.textContent = state.setting
        ? state.setting.style + (state.shape ? ' \u00b7 ' + state.shape : '') : 'Setting';

      if (key === 'stone') label.textContent = state.stone
        ? state.stone.carat.toFixed(2) + 'ct ' + state.stone.shape : 'Diamond';
      if (key === 'review') label.textContent = 'Ring';
    });
  }

  /* ---------- step 1: settings ---------- */

  /* A setting earns its place in the grid by having a picture. Photography
     lands on the Shopify product and build-ring-settings-index.mjs copies it
     into the index, so a setting appears here the moment its images are
     uploaded — there is no list to maintain. */
  var REQUIRE_PHOTO = root.getAttribute('data-require-photo') !== 'false';

  function hasPhoto(s) {
    return !!(s.image || (s.shape_images && Object.keys(s.shape_images).length));
  }

  function settingMatches(s) {
    if (REQUIRE_PHOTO && !hasPhoto(s)) return false;
    /* A shape chosen on the grid is a promise that every card shown is
       available in it — so a setting with no render for that shape is not
       shown at all, rather than shown with the wrong stone in it. */
    if (state.shape && s.shape_images && !s.shape_images[state.shape]) return false;
    if (state.styles.length && state.styles.indexOf(s.style_slug) === -1) return false;
    /* Against every metal the setting can be MADE in, not the one metal its
       default variant happens to be. Testing s.metal_slug meant picking
       "18k Rose Gold" emptied a grid in which all five settings offer it. */
    if (state.metals.length && !allowedMetals(s).length) return false;
    /* A setting spans $359.99 (10k) to $1,019.99 (platinum), so it belongs in
       a price band if any orderable metal of it lands there — comparing the
       single default price would hide a $389.99 ring from an "under $500"
       search because its 14k variant costs $569.99. */
    var range = priceRange(s);
    if (state.priceMin !== null && range[1] < state.priceMin) return false;
    if (state.priceMax !== null && range[0] > state.priceMax) return false;
    if (state.shapes.length && s.fits_shapes !== 'all') {
      var fits = s.fits_shapes.split(',');
      var any = state.shapes.some(function (sh) { return fits.indexOf(sh) > -1; });
      if (!any) return false;
    }
    // If a stone is already chosen, only show heads that actually hold it.
    if (state.stone) {
      if (state.stone.carat < s.carat_min || state.stone.carat > s.carat_max) return false;
      if (s.fits_shapes !== 'all' && s.fits_shapes.split(',').indexOf(state.stone.shape) === -1) return false;
    }
    return true;
  }

  function activeFilterCount() {
    return state.styles.length + state.metals.length + state.shapes.length +
      (state.priceMin !== null ? 1 : 0) + (state.priceMax !== null ? 1 : 0);
  }

  /* ---------- filter chips, built FROM the data ----------
     Hand-written style and metal lists in the section file drifted badly: five
     style chips with nothing behind them, one style (diamond-band) no chip
     could reach, and twelve of twenty-eight settings unreachable because their
     metal had no chip. Deriving both from the same index that fills the grid
     makes every one of those states impossible by construction, the same way
     presentValues() does it for the diamond filter's colour chips. */
  /* Chips are built from the settings the grid can actually show. Deriving them
     from every row instead would put back the exact dead-chip bug this whole
     approach exists to prevent: hiding the 28 photo-less settings would leave
     chips for Nature, Three Stone, Platinum and six other metals that no
     visible setting has. */
  function selectable() {
    return REQUIRE_PHOTO ? settings.filter(hasPhoto) : settings;
  }

  function distinct(key) {
    var seen = {};
    selectable().forEach(function (s) {
      var v = s[key];
      if (!v) return;
      if (!seen[v]) seen[v] = { value: v, count: 0, row: s };
      seen[v].count++;
    });
    return Object.keys(seen).map(function (k) { return seen[k]; })
      .sort(function (a, b) { return b.count - a.count || a.value.localeCompare(b.value); });
  }

  /* Every metal any visible setting can be MADE in, which is not the same as
     the metal of its default variant. Each of the five photographed settings
     is one product titled "... 14k Yellow Gold Setting" carrying ten metal
     variants, so distinct('metal_slug') saw a single value, decided a filter
     offering one choice is not a filter, and hid the Metal group entirely.
     That is also why every card asserted "14k Yellow Gold" at a visitor who
     had chosen no metal at all. */
  function metalOptions() {
    var seen = {};
    selectable().forEach(function (s) {
      metalsFor(s).forEach(function (m) {
        var slug = metalSlug(m.name);
        if (!seen[slug]) seen[slug] = { value: slug, name: m.name, count: 0, prices: [] };
        seen[slug].count++;
        seen[slug].prices.push(m.price);
      });
    });
    /* Tone-major, karat-ascending inside it — the reference builder groups the
       three golds of one colour together, and the data's own order would put
       10k Yellow next to 10k Rose. Platinum last, it belongs to no family. */
    var toneRank = { yellow: 0, rose: 1, white: 2, platinum: 3 };
    return Object.keys(seen).map(function (k) { return seen[k]; })
      .sort(function (a, b) {
        var at = a.value.split('-')[0], bt = b.value.split('-')[0];
        var d = (toneRank[at] === undefined ? 9 : toneRank[at]) -
                (toneRank[bt] === undefined ? 9 : toneRank[bt]);
        if (d) return d;
        return (parseInt(a.value, 10) || 0) - (parseInt(b.value, 10) || 0);
      });
  }

  /* The metals of one setting that the Metal filter currently allows. With no
     metal filter that is all of them, so the card's price range and the filter
     always describe the same set of rings. */
  function allowedMetals(s) {
    var list = metalsFor(s);
    if (!state.metals.length) return list;
    return list.filter(function (m) { return state.metals.indexOf(metalSlug(m.name)) > -1; });
  }

  /* [cheapest, dearest] across those metals. Falls back to the row price for
     the legacy seeded rows, which carry no variants. */
  function priceRange(s) {
    var list = allowedMetals(s);
    if (!list.length) return [s.price, s.price];
    var ps = list.map(function (m) { return m.price; });
    return [Math.min.apply(null, ps), Math.max.apply(null, ps)];
  }

  /* "9k White Gold" -> { karat: "9K", tone: "White Gold" }. The karat is shown
     as a badge and the tone as the label, so 9k/14k/18k of one colour read as
     one family instead of three unrelated chips. */
  function splitMetal(name) {
    var m = String(name).match(/^\s*(\d+\s*k|platinum)\s*(.*)$/i);
    if (!m) return { karat: '', tone: String(name) };
    if (/platinum/i.test(m[1])) return { karat: 'PT', tone: 'Platinum' };
    return { karat: m[1].replace(/\s+/g, '').toUpperCase(), tone: m[2] || name };
  }

  /* ---------- grid shape row ----------
     Not a filter in the usual sense. On the reference builder, choosing a shape
     re-renders every card with that setting holding that stone — the grid stops
     being a catalogue of designs and becomes a preview of YOUR ring in each of
     them. Our per-shape renders make the same thing possible. */
  /* The canonical shape order, in one place. It was previously read off the
     "Shape it fits" filter chips in two functions; deleting that filter left
     the detail page with no shape tiles at all, because only one of the two had
     a fallback. Prefer the diamond search's own chips (same vocabulary as the
     stones), then whatever the renders name. */
  function knownShapes() {
    var chips = Array.prototype.slice.call(
      document.querySelectorAll('[data-diamond-search] [data-shape]'))
      .map(function (b) { return b.getAttribute('data-shape'); })
      .filter(Boolean);
    if (chips.length) return chips;
    var seen = {};
    settings.forEach(function (o) {
      if (o.shape_images) Object.keys(o.shape_images).forEach(function (k) { seen[k] = 1; });
    });
    return Object.keys(seen);
  }

  function gridShapes() {
    var seen = {};
    selectable().forEach(function (s) {
      if (!s.shape_images) return;
      Object.keys(s.shape_images).forEach(function (sh) { seen[sh] = (seen[sh] || 0) + 1; });
    });
    var known = knownShapes();
    return known.filter(function (k) { return seen[k]; })
      .map(function (k) { return { shape: k, count: seen[k] }; });
  }

  function buildGridShapes() {
    var box = $('[data-grid-shapes]');
    if (!box) return;
    var opts = gridShapes();
    box.innerHTML = opts.map(function (o) {
      return '<button class="rs-tile" type="button" aria-pressed="' + (state.shape === o.shape) + '" ' +
        'data-grid-shape="' + esc(o.shape) + '">' + shapeIcon(o.shape) +
        '<span>' + esc(o.shape) + '</span></button>';
    }).join('');
    var row = box.closest('.rs-shaperow');
    if (row) row.hidden = opts.length < 2;
    paintGridShapeNote();
  }

  function paintGridShapeNote() {
    var note = $('[data-gridshape-note]');
    var clear = $('[data-clear-gridshape]');
    if (clear) clear.hidden = !state.shape;
    if (!note) return;
    note.textContent = state.shape
      ? 'Showing each setting made for ' + withArticle(state.shape) + ' diamond.'
      : 'Pick a shape to see every setting made for it. You can change it later.';
  }

  function buildFilterChips() {
    var styleBox = $('[data-style-tiles]');
    if (styleBox) {
      styleBox.innerHTML = distinct('style_slug').map(function (d) {
        return '<button class="rs-tile" type="button" aria-pressed="false" data-style="' +
          esc(d.value) + '">' + ringIcon(d.value) +
          '<span>' + esc(d.row.style) + '</span>' +
          '<i class="rs-tile__n">' + d.count + '</i></button>';
      }).join('');
    }

    var metalBox = $('[data-metal-tiles]');
    if (metalBox) {
      metalBox.innerHTML = metalOptions().map(function (d) {
        var m = splitMetal(d.name);
        return '<button class="rs-metal" type="button" aria-pressed="false" data-metal="' +
          esc(d.value) + '" data-metal-tone="' + esc(d.value) + '">' +
          '<i>' + esc(m.karat) + '</i><span>' + esc(m.tone) + '</span>' +
          '<b class="rs-tile__n">' + d.count + '</b></button>';
      }).join('');
    }

    /* The shape chips are written in Liquid (check-shapes.mjs guards them
       against STONE stock, which is a different question), so they can offer
       shapes no visible SETTING fits. With photo-only on, Moval, Triangular
       Brilliant and Lozenge Step Cut match nothing. Hide those rather than let
       a visitor click into a guaranteed empty grid. */
    /* A filter offering one choice is not a filter. Once the catalogue is
       photo-only there is a single metal, and a lone "14K Yellow Gold" chip
       just asks the visitor to confirm the obvious. */
    [['[data-style-tiles]', distinct('style_slug').length],
     ['[data-metal-tiles]', metalOptions().length]]
      .forEach(function (pair) {
        var box = $(pair[0]);
        if (!box) return;
        var group = box.closest('.rs-filter');
        /* Metal is counted from the variants now. Counting distinct
           metal_slug got 1 and hid the group, which is why there was no
           Metal filter on the page at all. */
        if (group) group.hidden = pair[1] < 2;
      });
    /* Price bands from the real spread, not round numbers plucked out of the
       air — a band with nothing in it is the same bug as a dead chip. */
    var bandBox = $('[data-price-bands]');
    var pool = selectable();
    if (bandBox && pool.length) {
      /* Every orderable price, not one per design: the spread that matters to
         a visitor runs from the cheapest 10k to the dearest platinum. */
      var prices = [];
      pool.forEach(function (s) {
        var ms = metalsFor(s);
        if (ms.length) ms.forEach(function (m) { prices.push(m.price); });
        else prices.push(s.price);
      });
      prices.sort(function (a, b) { return a - b; });
      var lo = prices[0], hi = prices[prices.length - 1];
      var step = Math.max(50, Math.round((hi - lo) / 3 / 50) * 50);
      var bands = [];
      for (var i = 0; i < 3 && lo + i * step < hi; i++) {
        var from = i === 0 ? null : lo + i * step;
        var to = i === 2 ? null : lo + (i + 1) * step;
        bands.push({ from: from, to: to });
      }
      bandBox.innerHTML = bands.map(function (b) {
        var label = b.from === null ? 'Under ' + money(b.to)
          : b.to === null ? money(b.from) + ' +'
          : money(b.from) + ' – ' + money(b.to);
        return '<button class="rs-band" type="button" aria-pressed="false" data-band="' +
          (b.from === null ? '' : b.from) + ':' + (b.to === null ? '' : b.to) + '">' +
          label + '</button>';
      }).join('');
    }
  }

  /* Chips are repainted from state rather than toggled in place, so the summary
     row, the panel and the state can never disagree about what is filtered. */
  function syncChips() {
    $$('[data-style]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(state.styles.indexOf(b.getAttribute('data-style')) > -1));
    });
    $$('[data-metal]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(state.metals.indexOf(b.getAttribute('data-metal')) > -1));
    });
    $$('[data-fitshape]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(state.shapes.indexOf(b.getAttribute('data-fitshape')) > -1));
    });
    $$('[data-band]').forEach(function (b) {
      var p = b.getAttribute('data-band').split(':');
      var from = p[0] === '' ? null : +p[0], to = p[1] === '' ? null : +p[1];
      b.setAttribute('aria-pressed', String(state.priceMin === from && state.priceMax === to));
    });
  }

  /* ---------- applied filters ----------
     Every active filter gets a removable chip. A visitor must never be looking
     at a shortened list because of something they cannot see — that reads as
     "they have no stock", which is the most expensive misreading on the page. */
  function paintApplied() {
    var box = $('[data-applied]');
    if (!box) return;
    var chips = [];

    state.styles.forEach(function (v) {
      var row = settings.filter(function (s) { return s.style_slug === v; })[0];
      chips.push({ label: row ? row.style : v, attr: 'style', value: v });
    });
    /* Named from the metal OPTIONS, the same source the chips are built from.
       Looking the name up on a row's metal_slug happened to work for the six
       metals some legacy seeded row also carries, and printed the raw slug
       ("yellow-10k") for the four it does not. */
    var metalNames = {};
    metalOptions().forEach(function (m) { metalNames[m.value] = m.name; });
    state.metals.forEach(function (v) {
      chips.push({ label: metalNames[v] || v, attr: 'metal', value: v });
    });
    state.shapes.forEach(function (v) {
      chips.push({ label: 'Fits ' + v, attr: 'fitshape', value: v });
    });
    if (state.priceMin !== null) chips.push({ label: 'From ' + money(state.priceMin), attr: 'price', value: 'min' });
    if (state.priceMax !== null) chips.push({ label: 'Up to ' + money(state.priceMax), attr: 'price', value: 'max' });

    box.hidden = !chips.length;
    box.innerHTML = chips.map(function (c) {
      return '<button class="rs-applied__chip" type="button" data-unfilter="' +
        c.attr + '" data-unfilter-value="' + esc(c.value) + '">' +
        esc(c.label) + '<span aria-hidden="true">&times;</span>' +
        '<span class="visually-hidden"> — remove filter</span></button>';
    }).join('');
  }

  /* Step 2 needs to say WHY the diamond list is shorter than the full search. */
  function paintContext() {
    var box = $('[data-context]');
    if (!box) return;
    if (!state.setting) { box.hidden = true; return; }
    box.hidden = false;
    var ico = $('[data-context-ico]');
    if (ico) ico.innerHTML = ringIcon(state.setting.style_slug);
    var scope = state.shape
      ? '<b>' + esc(state.shape.toLowerCase()) + '</b> diamonds'
      : state.setting.fits_shapes === 'all'
        ? 'diamonds of any shape'
        : '<b>' + esc(state.setting.fits_shapes.split(',').join(', ')) + '</b> diamonds';
    $('[data-context-text]').innerHTML =
      'Showing ' + scope + ' that fit your <b>' + esc(state.setting.style) + '</b> in ' +
      esc(state.setting.metal) + ', ' +
      state.setting.carat_min + '–' + state.setting.carat_max + 'ct.';
  }

  function paintSettings() {
    var list = settings.filter(settingMatches);

    var dir = state.sort.indexOf('-desc') > -1 ? -1 : 1;
    if (state.sort.indexOf('style') === 0) {
      list.sort(function (a, b) { return a.style.localeCompare(b.style) * dir; });
    } else {
      list.sort(function (a, b) { return (a.price - b.price) * dir; });
    }

    var n = activeFilterCount();
    var fc = $('[data-filter-count]');
    if (fc) {
      var open = fc.getAttribute('aria-expanded') === 'true';
      var lbl = fc.querySelector('span');
      if (lbl) lbl.textContent = (open ? 'Hide filters' : 'Show all filters') + (n ? ' (' + n + ')' : '');
      fc.setAttribute('data-has-filters', String(n > 0));
    }
    paintApplied();

    var grid = $('[data-settings]');
    var count = $('[data-setting-count]');
    if (count) {
      count.textContent = list.length + (list.length === 1 ? ' setting' : ' settings') +
        (state.stone ? ' that fit your stone' : '');
    }

    if (!list.length) {
      /* Always leave a way out. The grid is filtered by the chosen stone as
         well as by the chips, so "no results" can be caused by something the
         visitor did two steps ago — say which, and offer the undo. */
      var cause;
      if (state.stone && !n) {
        cause = 'None of our settings is made for a ' + state.stone.carat.toFixed(2) + 'ct ' +
          esc(state.stone.shape.toLowerCase()) + ' stone. Our settings take ' +
          'stones from 0.3ct upwards.';
      } else if (state.shape && !n) {
        cause = 'No setting is made for ' + esc(withArticle(state.shape)) + ' stone yet.';
      } else {
        cause = 'Nothing matches every filter at once.';
      }
      grid.innerHTML = '<div class="rs-empty" style="grid-column:1/-1">' +
        '<h3>No settings match</h3>' +
        '<p class="lede center-lede">' + cause + '</p>' +
        '<div class="rs-empty__acts">' +
          (n ? '<button class="btn btn--ghost" type="button" data-clear-filters>Clear all filters</button>' : '') +
          (state.shape && !state.stone ? '<button class="btn btn--ghost" type="button" data-clear-gridshape>Show all shapes</button>' : '') +
          (state.stone ? '<button class="btn btn--ghost" type="button" data-drop-stone>Choose a different diamond</button>' : '') +
        '</div></div>';
      return;
    }

    /* The card used to print the default variant's metal -- "14k Yellow Gold"
       -- as though the visitor had chosen it. They had not; the setting is
       made in ten metals. Show the metals it comes in, the way the reference
       builder does, and let the detail page be where one is chosen. */
    function metalDots(s) {
      var list = allowedMetals(s);
      if (!list.length) {
        return '<span class="rs-card__m"><i class="rs-swatch"></i>' + esc(s.metal) + '</span>';
      }
      /* One dot per tone, not per karat: ten dots is a colour chart, four is a
         glance. The karats are on the setting's own page. */
      var tones = [], seen = {};
      list.forEach(function (m) {
        var slug = metalSlug(m.name), tone = slug.split('-')[0];
        if (seen[tone]) return;
        seen[tone] = 1;
        tones.push({ tone: tone, slug: slug });
      });
      var n = list.length;
      return '<span class="rs-card__metals">' +
        tones.map(function (t) {
          return '<i class="rs-swatch" data-metal-tone="' + esc(t.slug) + '"></i>';
        }).join('') +
        '<span class="rs-card__metaln">' +
          n + (n === 1 ? ' metal' : ' metals') +
        '</span></span>';
    }

    /* "from $359.99" -- the cheapest orderable metal. A flat price would show
       the 14k figure while a 10k of the same ring sits $170 below it. */
    function cardPrice(s) {
      var r = priceRange(s);
      return (r[0] === r[1] ? '' : '<em class="rs-card__from">from</em> ') + exactMoney(r[0]);
    }

    grid.innerHTML = list.map(function (s) {
      /* Once a shape is chosen the grid IS that shape, so listing what else the
         head fits contradicts the row above it and pushes the setting type —
         the one thing the card is for — down the reading order. */
      var fits = state.shape ? '' :
        s.fits_shapes === 'all' ? 'Fits any shape'
          : 'Fits ' + s.fits_shapes.split(',').slice(0, 3).join(', ');
      var chosen = state.setting && state.setting.id === s.id;
      return '<button class="rs-card" type="button" data-pick-setting="' + s.id + '" ' +
        'data-metal-tone="' + esc(s.metal_slug) + '" ' +
        'aria-pressed="' + chosen + '">' +
        '<span class="rs-card__media">' +
          /* The shape picked on the grid drives the photo. Passing only the
             chosen STONE's shape meant the grid row filtered the cards but
             never re-rendered them — every card kept its default image, which
             is exactly the "I picked round and nothing changed" complaint. */
          settingMedia(s, state.shape || (state.stone && state.stone.shape)) +
          (chosen ? '<span class="rs-card__tick" aria-hidden="true">&#10003;</span>' : '') +
        '</span>' +
        '<span class="rs-card__body">' +
          '<span class="rs-card__t">' + esc(s.style) + '</span>' +
          metalDots(s) +
          (fits ? '<span class="rs-card__fits">' + esc(fits) + '</span>' : '') +
          '<span class="rs-card__foot">' +
            '<span class="rs-card__p">' + cardPrice(s) + '</span>' +
            '<span class="rs-card__cta">' + (chosen ? 'Selected · view' : 'View setting') + '</span>' +
          '</span>' +
        '</span></button>';
    }).join('');
  }

  /* ---------- step 1b: setting detail ----------
     The step the old build did not have. Its job is to make the commitment
     explicit: see the setting large, choose the stone shape it will hold, read
     that the price is for the setting alone, then press a button that says
     exactly what happens next. */
  var detailSetting = null;

  /* Which shapes this setting can be shown/ordered in: the shapes it has a
     render for, else the shapes its head fits, else every shape the filter
     knows. Same order as the shape chips, so the two never disagree. */
  function shapesFor(s) {
    var known = knownShapes();
    var pool;
    if (s.shape_images && Object.keys(s.shape_images).length) pool = Object.keys(s.shape_images);
    else if (s.fits_shapes && s.fits_shapes !== 'all') pool = s.fits_shapes.split(',').map(function (x) { return x.trim(); });
    else pool = known;
    return known.filter(function (k) { return pool.indexOf(k) > -1; });
  }

  /* The Description is open by default on a desktop, where it sits in a column
     beside the photo and costs nothing. Stacked, it pushed "Select this
     setting" more than two screens down, so it starts closed there. */
  function syncDescOpen() {
    var d = $('.rs-detail__desc');
    if (d) d.open = window.matchMedia('(min-width: 900px)').matches;
  }

  function openDetail(s) {
    detailSetting = s;
    var options = shapesFor(s);
    /* Keep a shape the visitor already chose if this setting offers it; if a
       stone is already picked, its shape is the only honest choice. */
    if (state.stone && options.indexOf(state.stone.shape) > -1) state.shape = state.stone.shape;
    else if (options.indexOf(state.shape) === -1) state.shape = options[0] || null;
    /* Filtering the grid to one metal and then opening a page showing 14k
       would quietly undo the choice. Carry it in; with several metals filtered
       start at the cheapest of them so the page agrees with the card's
       "from" price. */
    var allowed = allowedMetals(s);
    if (state.metals.length && allowed.length) {
      state.metal = allowed.reduce(function (a, b) { return b.price < a.price ? b : a; }).name;
    }
    $$('[data-browse]').forEach(function (el) { el.hidden = true; });
    var d = $('[data-detail]'); if (d) d.hidden = false;
    syncDescOpen();
    paintDetail();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function closeDetail() {
    detailSetting = null;
    var d = $('[data-detail]'); if (d) d.hidden = true;
    $$('[data-browse]').forEach(function (el) { el.hidden = false; });
    paintSettings();
  }

  /* Metals for a design are its sibling rows — each catalogue row is one
     (style, metal) pair, so "the same ring in rose gold" is a different row.
     Chosen here, on the product, never on the grid. */
  /* Metal is a VARIANT of the setting now, so the ladder lives on the row
     itself. Order comes from the data, which the import writes karat-ascending
     — re-sorting alphabetically would put 18k before 14k. */
  function metalsFor(s) {
    if (!s.metal_variants) return [];
    return Object.keys(s.metal_variants).map(function (name) {
      return { name: name, variant_id: s.metal_variants[name].variant_id,
               price: s.metal_variants[name].price };
    });
  }

  /* Which metal is being looked at. Defaults to the row's own metal so the
     photograph and the price agree on first paint. */
  function currentMetal(s) {
    var list = metalsFor(s);
    if (!list.length) return null;
    return list.filter(function (m) { return m.name === state.metal; })[0]
        || list.filter(function (m) { return m.name === s.metal; })[0]
        || list[0];
  }

  /* "18k Rose Gold" -> "rose-18k", matching the --metal-* tint tokens. */
  function metalSlug(name) {
    var n = String(name).toLowerCase();
    if (n.indexOf('platinum') > -1) return 'platinum';
    var karat = (n.match(/(\d+)\s*k/) || [])[1];
    var tone = n.indexOf('rose') > -1 ? 'rose' : n.indexOf('yellow') > -1 ? 'yellow' : 'white';
    return karat ? tone + '-' + karat + 'k' : tone;
  }

  function splitMetalLabel(name) { return splitMetal(name); }

  function paintDetail() {
    var s = detailSetting;
    if (!s) return;
    var options = shapesFor(s);
    /* Declared HERE, before anything reads it. It used to be declared down in
       the metal block, so the header line above ran against a hoisted
       `undefined` and always printed the row's own metal. */
    var metals = metalsFor(s);
    var active = currentMetal(s);

    $('[data-detail-media]').innerHTML = settingMedia(s, state.shape);
    $('[data-detail-title]').textContent = s.style;
    $('[data-detail-style]').textContent = s.style;
    $('[data-detail-metal]').textContent = active ? active.name : s.metal;
    var blurb = $('[data-detail-blurb]');
    if (blurb) { blurb.textContent = s.blurb || ''; blurb.hidden = !s.blurb; }
    $('[data-detail-price]').textContent = exactMoney(s.price);

    var locked = !!state.stone;
    var note = $('[data-detail-shape-note]');
    if (note) {
      note.textContent = locked
        ? 'Set to ' + state.stone.shape.toLowerCase() + ' to match the diamond you already chose.'
        : options.length > 1
          ? 'Choose the shape of diamond this setting will hold. The diamonds shown next will match it.'
          : 'This setting is made for a ' + String(options[0] || '').toLowerCase() + ' stone.';
    }

    $('[data-detail-shapes]').innerHTML = options.map(function (shape) {
      var on = shape === state.shape;
      return '<button class="rs-tile" type="button" aria-pressed="' + on + '" ' +
        'data-detail-shape="' + esc(shape) + '"' + (locked && !on ? ' disabled' : '') + '>' +
        shapeIcon(shape) + '<span>' + esc(shape) + '</span></button>';
    }).join('');

    /* Metal row: sibling rows of the same design. Hidden when there is only
       one — a lone chip asks the visitor to confirm the obvious. */
    var metalBox = $('[data-detail-metals]');
    var metalBlock = $('[data-detail-metal-block]');
    if (metalBlock) metalBlock.hidden = metals.length < 2;
    if (metalBox) {
      metalBox.innerHTML = metals.map(function (o) {
        var m = splitMetalLabel(o.name);
        return '<button class="rs-metal" type="button" aria-pressed="' +
          (!!active && o.name === active.name) + '" ' +
          'data-metal-option="' + esc(o.name) + '" data-metal-tone="' + esc(metalSlug(o.name)) + '">' +
          '<i>' + esc(m.karat) + '</i><span>' + esc(m.tone) + '</span></button>';
      }).join('');
    }
    /* Description + specs. Everything here is real data already on the row —
       no invented copy, and the make time comes from the section setting so it
       cannot contradict the review step. */
    var desc = $('[data-detail-desc]');
    if (desc) { desc.textContent = s.blurb || ''; desc.hidden = !s.blurb; }
    var specs = $('[data-detail-specs]');
    if (specs) {
      var fits = s.fits_shapes === 'all'
        ? 'Any shape we stock'
        : s.fits_shapes.split(',').map(function (x) { return x.trim(); }).join(', ');
      var sp = s.spec || {};
      var rows = [
        ['Style', s.style],
        ['Metal', active ? active.name : s.metal],
        ['Centre stone', fits]
      ];
      /* Band width applies to every setting. The side-stone rows exist only
         on settings that HAVE side stones — a solitaire has none, and an
         empty "Side stone clarity: —" row is worse than no row at all. */
      if (sp.band_width) rows.push(['Band width', sp.band_width]);
      if (sp.band_thickness) rows.push(['Band thickness', sp.band_thickness]);
      if (sp.side_stone_shape) rows.push(['Side stones', sp.side_stone_shape]);
      if (sp.side_stone_weight) rows.push(['Side stone weight', sp.side_stone_weight]);
      if (sp.side_stone_size) rows.push(['Side stone size', sp.side_stone_size]);
      if (sp.side_stone_colour) rows.push(['Side stone colour', sp.side_stone_colour]);
      if (sp.side_stone_clarity) rows.push(['Side stone clarity', sp.side_stone_clarity]);
      rows.push(['Made', 'To order, for the diamond you choose']);
      specs.innerHTML = rows.map(function (r) {
        return '<dt>' + esc(r[0]) + '</dt><dd>' + esc(r[1]) + '</dd>';
      }).join('');
    }

    var mNote = $('[data-detail-metal-note]');
    if (mNote) mNote.textContent = active ? active.name : '';
    /* The price follows the metal — that is the whole point of the ladder. */
    if (active) $('[data-detail-price]').textContent = exactMoney(active.price);

    var btn = $('[data-select-setting]');
    if (btn) {
      btn.innerHTML = state.stone
        ? 'Select this setting &rarr; review your ring'
        : 'Select this setting &rarr; choose ' +
          (state.shape ? esc(withArticle(state.shape)) + ' ' : 'a ') + 'diamond';
    }
  }

  function commitDetail() {
    if (!detailSetting) return;
    if (!state.stone) state.path = 'setting';   // first half chosen wins the order
    state.setting = detailSetting;
    save();
    closeDetail();
    paintSteps(); paintBar();
    go(state.stone ? 3 : 2);
  }

  /* ---------- finger size ----------
     One table, three regions. The reference builder asks for a region first and
     then the sizes for it, with an explicit "Not sure" instead of a silent
     default — a ring quietly made to a size nobody chose is the worst outcome
     on this page.

     US is the value we manufacture to; UK and EU are LABELS for the same row,
     so the conversions cannot drift apart the way two separate lists would.
     These are the standard trade equivalents, not interpolated. */
  var SIZE_TABLE = [
    { us: '3',    uk: 'F',    eu: '44' },
    { us: '3.5',  uk: 'G',    eu: '45.5' },
    { us: '4',    uk: 'H',    eu: '46.5' },
    { us: '4.5',  uk: 'I',    eu: '48' },
    { us: '5',    uk: 'J½',   eu: '49' },
    { us: '5.5',  uk: 'K½',   eu: '50' },
    { us: '6',    uk: 'L½',   eu: '51.5' },
    { us: '6.5',  uk: 'M½',   eu: '52.5' },
    { us: '7',    uk: 'N½',   eu: '54' },
    { us: '7.5',  uk: 'O½',   eu: '55.5' },
    { us: '8',    uk: 'P½',   eu: '57' },
    { us: '8.5',  uk: 'Q½',   eu: '58' },
    { us: '9',    uk: 'R½',   eu: '59' },
    { us: '9.5',  uk: 'S½',   eu: '60.5' },
    { us: '10',   uk: 'T½',   eu: '62' },
    { us: '10.5', uk: 'U½',   eu: '63' },
    { us: '11',   uk: 'V½',   eu: '64.5' },
    { us: '11.5', uk: 'W½',   eu: '65.5' },
    { us: '12',   uk: 'X½',   eu: '67' },
    { us: '12.5', uk: 'Y½',   eu: '68' },
    { us: '13',   uk: 'Z½',   eu: '69' }
  ];
  var REGIONS = [
    { id: 'US', label: 'US / Canada' },
    { id: 'UK', label: 'UK / Australia' },
    { id: 'EU', label: 'Europe' }
  ];
  var UNSURE = 'unsure';
  var UNSURE_US = '6';        // what "Not sure" is actually made to

  function sizeLabel(row, region) {
    if (region === 'UK') return row.uk;
    if (region === 'EU') return row.eu;
    return row.us;
  }

  function buildSizeControls() {
    var regionSel = $('[data-size-region]');
    var sizeSel = $('[data-size]');
    if (!regionSel || !sizeSel) return;

    if (!regionSel.options.length) {
      regionSel.innerHTML = REGIONS.map(function (r) {
        return '<option value="' + r.id + '">' + esc(r.label) + '</option>';
      }).join('');
      regionSel.value = state.region || 'US';
    }
    paintSizeOptions();
  }

  function paintSizeOptions() {
    var regionSel = $('[data-size-region]');
    var sizeSel = $('[data-size]');
    if (!regionSel || !sizeSel) return;
    var region = regionSel.value || 'US';

    sizeSel.innerHTML =
      '<option value="">Select ring size</option>' +
      '<option value="' + UNSURE + '">Not sure — make it to a standard size</option>' +
      SIZE_TABLE.map(function (row) {
        return '<option value="' + row.us + '">' + esc(sizeLabel(row, region)) + '</option>';
      }).join('');
    sizeSel.value = state.size || '';
    paintSizeConversion();
  }

  /* Says, in words, exactly what will be made — the point where a conversion
     table either reassures or terrifies. */
  function paintSizeConversion() {
    var el = $('[data-size-conv]');
    if (!el) return;
    var regionSel = $('[data-size-region]');
    var region = regionSel ? regionSel.value : 'US';

    if (state.size === UNSURE) {
      el.textContent = 'We will make it to US ' + UNSURE_US +
        ' and resize once, free, within 60 days.';
      el.hidden = false;
      return;
    }
    var row = SIZE_TABLE.filter(function (r) { return r.us === state.size; })[0];
    if (!row) { el.hidden = true; return; }
    el.hidden = false;
    el.textContent = region === 'US'
      ? 'US ' + row.us + ' — UK ' + row.uk + ', EU ' + row.eu + '.'
      : (region === 'UK' ? 'UK ' + row.uk : 'EU ' + row.eu) +
        ' — made to US ' + row.us + '.';
  }

  /* What goes on the cart line and in front of the workshop. */
  function sizeForCart() {
    if (state.size === UNSURE) return 'US ' + UNSURE_US + ' (customer unsure)';
    var row = SIZE_TABLE.filter(function (r) { return r.us === state.size; })[0];
    if (!row) return null;
    var regionSel = $('[data-size-region]');
    var region = regionSel ? regionSel.value : 'US';
    return region === 'US' ? 'US ' + row.us
      : 'US ' + row.us + ' (' + region + ' ' + sizeLabel(row, region) + ')';
  }

  function sizeChosen() { return state.size === UNSURE || !!sizeForCart(); }

  /* ---------- step 3: review ---------- */
  function certificationFee() {
    var choice = $('[data-certification-request]'), panel = $('[data-certification-option]');
    return state.stone && !(state.stone.lab && state.stone.cert) && choice && choice.checked && panel.dataset.stone === String(state.stone.variant_id || state.stone.id) ? 25 : 0;
  }
  root.addEventListener('change', function(event) {
    if (event.target.matches('[data-certification-request]')) {paintReview();paintBar();}
  });

  function paintReview() {
    if (!state.setting || !state.stone) return;
    buildSizeControls();
    paintAddButton();
    /* Price and metal both follow the metal chosen on the setting page. */
    var settingMetal = currentMetal(state.setting);
    var settingPrice = settingMetal ? settingMetal.price : state.setting.price;
    var total = settingPrice + state.stone.price + certificationFee();

    /* By step 3 the stone is known, so the review shows the render of this
       setting holding this shape — the closest thing to a photo of the ring
       they are about to buy.

       The caption is rebuilt rather than left in the markup: assigning
       innerHTML here used to delete it outright, so the honesty note never
       actually reached anyone. Its wording depends on what is being shown —
       calling a photograph "an illustration" is its own small lie. */
    var shownShape = state.stone.shape;
    var isPhoto = !!settingImage(state.setting, shownShape);
    /* "a emerald stone" — the shape names come from the feed, so the article
       has to be worked out rather than written into the sentence. */
    var note = isPhoto
      ? 'Shown with ' + esc(withArticle(shownShape)) + ' stone. Your ring is made to ' +
        'order with the diamond you chose, so the stone will differ slightly.'
      : 'Illustration of the setting style. Your ring is made to order with the ' +
        'stone you chose.';
    $('[data-preview]').innerHTML = settingMedia(state.setting, shownShape) +
      '<p class="rs-preview__note">' + note + '</p>';

    $('[data-lines]').innerHTML =
      /* The row's `title` still bakes in a metal ("Beaded Band Solitaire 14k
         Yellow Gold Setting") from before metal became a variant, so it
         contradicted the metal on the line below it. The style is the name;
         the metal is the sub-line's job. */
      line(ringIcon(state.setting.style_slug), state.setting.style,
           (settingMetal ? settingMetal.name : state.setting.metal) +
             ' · fits ' + state.setting.carat_min + '–' + state.setting.carat_max + 'ct',
           settingPrice, 1, 'setting') +
      line(shapeIcon(state.stone.shape),
           state.stone.carat.toFixed(2) + 'ct ' + state.stone.shape + ' ' + state.stone.growth + ' Diamond',
           'Colour ' + state.stone.color + ' · Clarity ' + state.stone.clarity +
             (state.stone.cut ? ' · Cut ' + state.stone.cut : '') + ' · ' + (state.stone.lab && state.stone.cert ? state.stone.lab + ' ' + state.stone.cert : 'Non-certified'),
           state.stone.price, 2, 'stone');

    var certPanel = $('[data-certification-option]');
    var certRequest = $('[data-certification-request]');
    if (certPanel) {
      certPanel.hidden = !!(state.stone.lab && state.stone.cert);
      var chosenId = String(state.stone.variant_id || state.stone.id);
      if (certPanel.dataset.stone !== chosenId && certRequest) certRequest.checked = false;
      certPanel.dataset.stone = chosenId;
    }
    if (certificationFee()) $('[data-lines]').insertAdjacentHTML('beforeend', '<div class="rs-line"><span>Optional certification</span><span class="rs-line__p">$25.00</span></div>');
    $('[data-total]').textContent = money(total);
  }

  /* The button says what is still needed rather than sitting there disabled
     with no explanation — the reference builder's CTA reads "Select region"
     until a size is chosen. */
  function paintAddButton() {
    var btn = $('[data-add-ring]');
    if (!btn) return;
    var ok = sizeChosen();
    btn.disabled = !ok;
    btn.textContent = ok ? 'Add ring to cart' : 'Choose a finger size';
  }

  function line(ico, title, sub, price, step, half) {
    if (half !== 'setting' && half !== 'stone') {
      throw new Error('line() needs half="setting"|"stone" \u2014 got ' + half);
    }
    return '<div class="rs-line">' +
      '<span class="rs-line__ico">' + ico + '</span>' +
      '<span><span class="rs-line__t">' + esc(title) + '</span><br>' +
        '<span class="rs-line__s">' + esc(sub) + '</span><br>' +
        '<span class="rs-line__acts">' +
          '<button class="rs-line__edit" type="button" data-goto="' + step + '">Change</button>' +
          '<button class="rs-line__remove" type="button" data-remove="' + half + '">Remove</button>' +
        '</span></span>' +
      '<span class="rs-line__p">' + money(price) + '</span></div>';
  }

  /* ---------- running total ----------
     Shown the moment the first choice is made. Hiding the price until step 3
     is what makes a builder feel like a trap. */
  function paintBar() {
    var bar = $('[data-bar]');
    if (!bar) return;
    var any = state.setting || state.stone;
    bar.setAttribute('data-show', String(!!any));
    if (!any) return;

    /* Declared BEFORE the label that reads it. Declaring it lower down left the
       label using a hoisted-but-undefined value, so the bar kept naming the
       row's own metal while the total below it used the chosen one. */
    var barMetal = state.setting ? currentMetal(state.setting) : null;

    $('[data-bar-setting]').innerHTML = state.setting
      ? '<small>Setting</small><b>' + esc(state.setting.style) + ' · ' +
        esc(barMetal ? barMetal.name : state.setting.metal) + '</b>'
      : '<small>Setting</small><b>Not chosen</b>';
    $('[data-bar-stone]').innerHTML = state.stone
      ? '<small>Diamond</small><b>' + state.stone.carat.toFixed(2) + 'ct ' + esc(state.stone.shape) +
        ' · ' + esc(state.stone.color) + '/' + esc(state.stone.clarity) + '</b>'
      : '<small>Diamond</small><b>Not chosen</b>';

    /* The bar's two remove buttons only exist once there is something to
       remove. Kept next to each half rather than inside it — a button cannot
       be nested in a button. */
    var rmSetting = $('[data-bar-remove="setting"]');
    if (rmSetting) rmSetting.hidden = !state.setting;
    var rmStone = $('[data-bar-remove="stone"]');
    if (rmStone) rmStone.hidden = !state.stone;

    var settingCost = state.setting ? (barMetal ? barMetal.price : state.setting.price) : 0;
    var total = settingCost + (state.stone ? state.stone.price : 0) + certificationFee();
    $('[data-bar-total]').innerHTML =
      '<small>' + (state.setting && state.stone ? 'Total' : 'So far') + '</small><b>' + money(total) + '</b>';

    /* The forward CTA. The previous build bound a [data-next] handler but never
       rendered the button, so once a setting was chosen the only way onward was
       to guess that the step numbers were clickable. */
    var next = $('[data-next]');
    if (!next) return;
    var target = null, label = '';
    if (state.setting && state.stone && state.step !== 3) { target = 3; label = 'Review your ring'; }
    else if (state.setting && !state.stone && state.step === 1) { target = 2; label = 'Choose a diamond'; }
    else if (state.stone && !state.setting && state.step === 2) { target = 1; label = 'Choose a setting'; }
    next.hidden = !target;
    if (target) { next.setAttribute('data-next', String(target)); next.textContent = label; }
  }

  /* ---------- sourcing fallback ----------
     What the visitor asked for is worth more than the fact they left. When the
     diamond step returns nothing — and for the commonest request of all, a 1ct
     white round, it currently returns nothing — this turns the dead end into a
     request we can answer, pre-filled from the search they already built. */
  var lastQuery = { count: null, query: '' };

  function paintSource() {
    var box = $('[data-source]');
    if (!box) return;

    var none = lastQuery.count === 0;
    var thin = lastQuery.count !== null && lastQuery.count > 0 && lastQuery.count <= 12;
    var mode = none ? 'loud' : thin ? 'nudge' : 'quiet';
    var was = box.getAttribute('data-mode');
    box.setAttribute('data-mode', mode);

    /* Going loud means the results above just emptied. Bring the panel to the
       visitor rather than leaving it below an empty grid they may not scroll
       past — but only on the transition, or every keystroke would yank the
       page around. Never while they are typing in it. */
    if (mode === 'loud' && was !== 'loud' && state.step === 2 &&
        !box.contains(document.activeElement)) {
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    var title = $('[data-source-title]');
    var lede = $('[data-source-lede]');
    if (title && none) title.textContent = 'We do not have that stone in stock';
    else if (title && thin) title.textContent = 'Only a few match — want us to look further?';
    else if (title) title.textContent = box.getAttribute('data-title-default') || title.textContent;

    if (lede && none) {
      lede.textContent = 'Nothing in stock matches that right now. We buy to order as ' +
        'well as from stock, so tell us what you want and we will come back with ' +
        'what we can get and what it would cost.';
    }

    /* Pre-fill, but never overwrite something the visitor has typed. */
    var body = $('[data-source-body]');
    if (body && !body.dataset.touched) {
      var lines = [];
      if (lastQuery.query) lines.push('Looking for: ' + lastQuery.query);
      if (state.setting) lines.push('To set in: ' + state.setting.title);
      body.value = lines.join('\n');
    }
    var note = $('[data-source-note]');
    if (note) {
      note.textContent = lastQuery.count === null ? ''
        : lastQuery.count + (lastQuery.count === 1 ? ' stone' : ' stones') +
          ' currently match these filters. Edit the note above if this is not quite right.';
    }
  }

  function bindSource() {
    var box = $('[data-source]');
    if (!box) return;
    var title = $('[data-source-title]');
    if (title) box.setAttribute('data-title-default', title.textContent);

    var body = $('[data-source-body]');
    if (body) body.addEventListener('input', function () { body.dataset.touched = '1'; });

    document.addEventListener('diamond:results', function (e) {
      lastQuery = { count: e.detail.count, query: e.detail.query };
      paintSource();
    });

    /* Catch up on the render that happened before this listener existed. */
    var search = document.querySelector('[data-diamond-search]');
    if (search && search.lastResults) {
      lastQuery = { count: search.lastResults.count, query: search.lastResults.query };
      paintSource();
    }

    /* Submitted over fetch so a request does not cost the visitor the ring they
       have been building — a normal POST navigates away to the contact page.
       The form still works without JS; this only intercepts when it can. */
    var form = box.querySelector('form');
    if (!form || !window.FormData) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = $('[data-source-submit]');
      var status = $('[data-source-status]');
      var original = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
      if (status) { status.textContent = ''; status.removeAttribute('data-state'); }

      fetch(form.action, { method: 'POST', body: new FormData(form) })
        .then(function (r) {
          if (!r.ok) throw new Error('That did not send. Please try again, or email us directly.');
          form.reset();
          if (body) delete body.dataset.touched;
          box.setAttribute('data-sent', 'true');
          if (status) {
            status.setAttribute('data-state', 'ok');
            status.textContent = 'Thank you — we have your request and will reply within one business day.';
          }
        })
        .catch(function (err) {
          if (status) {
            status.setAttribute('data-state', 'err');
            status.textContent = err.message;
          }
        })
        .then(function () {
          if (btn) { btn.disabled = false; btn.textContent = original; }
        });
    });
  }

  /* ---------- layout ----------
     The filters stay open where there is room to show them beside the grid —
     the earlier build's reasoning still holds, that hiding them puts a door in
     front of the thing the visitor came for. Below 900px an open drawer pushes
     the grid entirely off-screen, so there it starts collapsed. */
  function initLayout() {
    var box = $('.rs-filters');
    var toggle = $('[data-filter-count]');
    /* Filters start CLOSED, as on the reference builder. The earlier call to
       open them on desktop made sense against 28 settings; against a handful
       it put a wall of chips between the visitor and the rings. And below a
       few settings there is nothing to filter, so the control goes entirely —
       a filter with five things behind it is a puzzle, not a tool. */
    /* The test is "is there anything worth filtering", NOT "are there many
       settings". Counting settings hid the entire filter control -- toggle and
       all -- behind a catalogue of five, which is why the page appeared to
       have no filters at all. Those five are made in ten metals and span
       $359.99 to $1,019.99: that is precisely what a visitor wants to narrow.
       Ask each group whether it actually offers a choice. */
    var prices = [];
    selectable().forEach(function (s) {
      var ms = metalsFor(s);
      if (ms.length) ms.forEach(function (m) { prices.push(m.price); });
      else prices.push(s.price);
    });
    var spread = prices.length > 1 &&
      Math.max.apply(null, prices) > Math.min.apply(null, prices);
    var nothing = distinct('style_slug').length < 2 && metalOptions().length < 2 && !spread;
    /* OPEN by default. Closed, the filters were a button most visitors never
       pressed; open, the choices are the first thing under the shape row. The
       cost is vertical space, which is why every tile group is a single
       horizontally-scrolling row rather than a wrapping block -- see
       .rs-tiles in the CSS. Kept collapsible, so it can still be got out of
       the way. */
    if (box) { box.setAttribute('data-open', String(!nothing)); box.hidden = nothing; }
    if (toggle) { toggle.setAttribute('aria-expanded', String(!nothing)); toggle.hidden = nothing; }
    var applied = $('[data-applied]');
    if (applied && nothing) applied.hidden = true;

    /* The toolbar sticks below the step rail, whose height changes between
       breakpoints — measure it rather than hard-coding a number that goes
       stale the moment the rail is restyled. */
    var steps = $('.rs-steps');
    var bar = $('[data-bar]');
    if (!steps) return;
    var setH = function () {
      root.style.setProperty('--rs-steps-h', steps.offsetHeight + 'px');
      /* The bar is sticky at the bottom and overlays whatever scrolls under it,
         so the active panel reserves exactly its height — measured, because the
         bar wraps to two rows on narrow screens. */
      if (bar) root.style.setProperty('--rs-bar-h', bar.offsetHeight + 'px');
    };
    setH();
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(setH);
      ro.observe(steps);
      if (bar) ro.observe(bar);
    } else {
      window.addEventListener('resize', setH);
    }
  }

  /* ---------- wiring ---------- */
  function bind() {
    /* Both of the first two chips are live from the start, and whichever the
       visitor opens FIRST becomes step 1 — that is how the reference builder
       behaves, and it replaces the old "start with the diamond instead" link.
       Once either half is actually chosen the order locks, so the rail cannot
       reshuffle under someone mid-build. */
    $$('[data-step-go]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.disabled) return;
        var key = b.getAttribute('data-step-go');
        if ((key === 'setting' || key === 'stone') && !state.setting && !state.stone) {
          state.path = key;
        }
        go(PANEL[key]);
      });
    });

    root.addEventListener('click', function (e) {
      /* A card opens the setting large; it no longer commits anything. The
         commitment is the "Select this setting" button on the detail, after
         the visitor has chosen a shape and read what the price covers. */
      var pick = e.target.closest('[data-pick-setting]');
      if (pick) {
        var id = +pick.getAttribute('data-pick-setting');
        var found = settings.filter(function (s) { return s.id === id; })[0];
        if (found) openDetail(found);
        return;
      }
      if (e.target.closest('[data-back-to-grid]')) { closeDetail(); return; }
      if (e.target.closest('[data-select-setting]')) { commitDetail(); return; }
      var metalPick = e.target.closest('[data-metal-option]');
      if (metalPick) {
        state.metal = metalPick.getAttribute('data-metal-option');
        save(); paintDetail();
        return;
      }
      var shapePick = e.target.closest('[data-detail-shape]');
      if (shapePick) {
        state.shape = shapePick.getAttribute('data-detail-shape');
        paintDetail();
        return;
      }
      var goto = e.target.closest('[data-goto]');
      if (goto) { go(+goto.getAttribute('data-goto')); return; }
      var next = e.target.closest('[data-next]');
      if (next) { go(+next.getAttribute('data-next')); }
    });

    // Step 2 is the real diamond search, running in select mode.
    document.addEventListener('diamond:select', function (e) {
      if (!state.setting) state.path = 'stone';  // first half chosen wins the order
      state.stone = e.detail;
      /* The stone decides the shape from here on — the reference builder
         pre-applies it to the setting grid so every card shown is one that
         actually holds the diamond just chosen. Without this the grid opened
         unfiltered and, on the diamond-first path, empty. */
      if (state.stone && state.stone.shape) state.shape = state.stone.shape;
      save();
      buildGridShapes(); paintSteps(); paintSettings(); paintBar();
      go(state.setting ? 3 : 1);
    });

    /* Delegated, not bound per element: the style and metal chips are built
       from the index after this runs, so anything bound directly would miss
       them entirely. */
    var MULTI = { style: 'styles', metal: 'metals', fitshape: 'shapes' };
    root.addEventListener('click', function (e) {
      var hit, attr;
      for (attr in MULTI) {
        hit = e.target.closest('[data-' + attr + ']');
        if (hit) break;
      }
      if (!hit) return;
      var key = MULTI[attr];
      var v = hit.getAttribute('data-' + attr);
      var i = state[key].indexOf(v);
      if (i > -1) state[key].splice(i, 1); else state[key].push(v);
      syncChips();
      paintSettings();
    });

    root.addEventListener('click', function (e) {
      var g = e.target.closest('[data-grid-shape]');
      if (g) {
        var v = g.getAttribute('data-grid-shape');
        state.shape = state.shape === v ? null : v;
        save(); buildGridShapes(); paintSettings(); paintSteps();
        return;
      }
      if (e.target.closest('[data-clear-gridshape]')) {
        state.shape = null;
        save(); buildGridShapes(); paintSettings(); paintSteps();
      }
    });

    /* Remove one applied filter from its chip in the summary row. */
    root.addEventListener('click', function (e) {
      var x = e.target.closest('[data-unfilter]');
      if (!x) return;
      var attr = x.getAttribute('data-unfilter');
      var v = x.getAttribute('data-unfilter-value');
      if (attr === 'price') {
        if (v === 'min') { state.priceMin = null; var pn = $('[data-price-min]'); if (pn) pn.value = ''; }
        else { state.priceMax = null; var px = $('[data-price-max]'); if (px) px.value = ''; }
      } else {
        var key = MULTI[attr];
        var i = state[key].indexOf(v);
        if (i > -1) state[key].splice(i, 1);
      }
      syncChips();
      paintSettings();
    });

    /* Price bands are a shortcut into the same two inputs, never a third
       source of truth for the price filter. */
    root.addEventListener('click', function (e) {
      var b = e.target.closest('[data-band]');
      if (!b) return;
      var parts = b.getAttribute('data-band').split(':');
      var from = parts[0] === '' ? null : +parts[0];
      var to = parts[1] === '' ? null : +parts[1];
      var already = state.priceMin === from && state.priceMax === to;
      state.priceMin = already ? null : from;
      state.priceMax = already ? null : to;
      var pn = $('[data-price-min]'), px = $('[data-price-max]');
      if (pn) pn.value = state.priceMin === null ? '' : '$' + state.priceMin;
      if (px) px.value = state.priceMax === null ? '' : '$' + state.priceMax;
      paintRange();
      syncChips();
      paintSettings();
    });

    /* ---------- price slider ----------
       Two range inputs stacked on one track. The pair is kept ordered on every
       move, so a visitor cannot drag the minimum past the maximum and empty
       the grid with a gesture that looks like nothing happened. */
    var RANGE_STEP = 10;

    function rangeBounds() {
      var prices = [];
      selectable().forEach(function (s) {
        var ms = metalsFor(s);
        if (ms.length) ms.forEach(function (m) { prices.push(m.price); });
        else prices.push(s.price);
      });
      if (!prices.length) return null;
      var lo = Math.floor(Math.min.apply(null, prices) / RANGE_STEP) * RANGE_STEP;
      var hi = Math.ceil(Math.max.apply(null, prices) / RANGE_STEP) * RANGE_STEP;
      return hi > lo ? [lo, hi] : null;
    }

    function buildRange() {
      var box = $('[data-price-range]');
      if (!box) return;
      var b = rangeBounds();
      /* One price in the catalogue is not a range. */
      box.hidden = !b;
      if (!b) return;
      $$('[data-range]', box).forEach(function (i) {
        i.min = b[0]; i.max = b[1]; i.step = RANGE_STEP;
      });
      paintRange();
    }

    function paintRange() {
      var box = $('[data-price-range]');
      if (!box || box.hidden) return;
      var b = rangeBounds();
      if (!b) return;
      var lo = state.priceMin === null ? b[0] : Math.max(b[0], state.priceMin);
      var hi = state.priceMax === null ? b[1] : Math.min(b[1], state.priceMax);
      var mn = $('[data-range="min"]', box), mx = $('[data-range="max"]', box);
      if (mn) mn.value = lo;
      if (mx) mx.value = hi;
      var fill = $('[data-range-fill]', box);
      if (fill) {
        var span = b[1] - b[0];
        fill.style.left = ((lo - b[0]) / span * 100) + '%';
        fill.style.right = ((b[1] - hi) / span * 100) + '%';
      }
    }

    root.addEventListener('input', function (e) {
      var i = e.target.closest ? e.target.closest('[data-range]') : null;
      if (!i) return;
      var box = $('[data-price-range]');
      var b = rangeBounds();
      if (!b) return;
      var mn = $('[data-range="min"]', box), mx = $('[data-range="max"]', box);
      var lo = +mn.value, hi = +mx.value;
      /* Push the other handle rather than swapping the values: swapping makes
         the handle you are dragging jump out from under the cursor. */
      if (lo > hi) {
        if (i.getAttribute('data-range') === 'min') hi = lo; else lo = hi;
      }
      /* At the ends the filter is "no limit", not "limit that happens to equal
         the extreme" — otherwise the applied-filters row shows a chip the
         visitor never set. */
      state.priceMin = lo <= b[0] ? null : lo;
      state.priceMax = hi >= b[1] ? null : hi;
      var pn = $('[data-price-min]'), px = $('[data-price-max]');
      if (pn) pn.value = state.priceMin === null ? '' : '$' + state.priceMin;
      if (px) px.value = state.priceMax === null ? '' : '$' + state.priceMax;
      paintRange();
      syncChips();
      paintSettings();
    });

    /* bind() runs after the index has loaded, so the real spread is known. */
    buildRange();

    /* Clears the FILTERS only — the chosen setting and stone survive, which is
       what "clear filters" means everywhere else on the web. Start over is a
       separate, clearly-labelled button. */
    root.addEventListener('click', function (e) {
      if (!e.target.closest('[data-clear-filters]')) return;
      state.styles = []; state.metals = []; state.shapes = [];
      state.priceMin = null; state.priceMax = null;
      var pn = $('[data-price-min]'), px = $('[data-price-max]');
      if (pn) pn.value = ''; if (px) px.value = '';
      paintRange();
      syncChips();
      paintSettings();
    });

    /* Remove one half of the ring. Removing sends you to the step that half
       belongs to rather than leaving you on a review of half a ring — and it
       keeps the other half, which is the whole point of removing rather than
       starting over. */
    root.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-remove]');
      if (!rm) return;
      var half = rm.getAttribute('data-remove');
      if (half === 'setting') {
        state.setting = null;
        detailSetting = null;
        var d = $('[data-detail]'); if (d) d.hidden = true;
        $$('[data-browse]').forEach(function (el) { el.hidden = false; });
      } else {
        state.stone = null;
      }
      save();
      buildGridShapes(); paintSteps(); paintSettings(); paintBar();
      /* Straight back to choosing the thing that was just removed. */
      go(half === 'setting' ? 1 : 2);
    });

    /* Escape hatch from the "no setting holds your stone" dead end. */
    root.addEventListener('click', function (e) {
      if (!e.target.closest('[data-drop-stone]')) return;
      state.stone = null;
      save(); syncChips(); paintSteps(); paintSettings(); paintBar();
      go(2);
    });

    var sort = $('[data-sort-settings]');
    if (sort) sort.addEventListener('change', function () {
      state.sort = sort.value; paintSettings();
    });

    ['min', 'max'].forEach(function (which) {
      var el = $('[data-price-' + which + ']');
      if (!el) return;
      el.addEventListener('change', function () {
        var v = parseFloat(el.value.replace(/[^0-9.]/g, ''));
        state[which === 'min' ? 'priceMin' : 'priceMax'] = isNaN(v) ? null : v;
        /* A typed budget moves the handles too, or the two controls sit side
           by side disagreeing about the same number. */
        paintRange();
        syncChips();
        paintSettings();
      });
    });

    var ft = $('[data-filter-count]');
    if (ft) ft.addEventListener('click', function () {
      var box = $('.rs-filters');
      var open = box.getAttribute('data-open') !== 'true';
      box.setAttribute('data-open', String(open));
      ft.setAttribute('aria-expanded', String(open));
      paintSettings();          // relabels the toggle
    });

    // Starting from a diamond is one quiet link now, not a two-tile fork the
    // visitor has to resolve before seeing a single ring.
    $$('[data-path="stone"]').forEach(function (b) {
      b.addEventListener('click', function () {
        state.path = 'stone';
        state.shape = null;             // the stone will decide the shape
        go(2);
      });
    });

    var size = $('[data-size]');
    if (size) size.addEventListener('change', function () {
      state.size = size.value;
      save(); paintSizeConversion(); paintReview();
    });
    var regionSel = $('[data-size-region]');
    if (regionSel) regionSel.addEventListener('change', function () {
      state.region = regionSel.value;
      save(); paintSizeOptions();
    });

    var add = $('[data-add-ring]');
    if (add) add.addEventListener('click', function () { addRing(add); });

    // Without this there is no way out of a choice: the setting grid is filtered
    // by the chosen stone, so picking a 5ct stone hides almost every setting and
    // leaves the visitor stuck.
    var clear = $('[data-clear-ring]');
    if (clear) clear.addEventListener('click', function () {
      state.setting = null; state.stone = null; state.shape = null;
      state.styles = []; state.metals = []; state.shapes = [];
      closeDetail();
      state.priceMin = null; state.priceMax = null;
      state.path = 'setting';
      try { localStorage.removeItem(KEY); } catch (e) { /* storage may be blocked */ }
      try { history.replaceState(null, '', location.pathname); } catch (e) { /* data: origin */ }

      $$('[data-style],[data-metal],[data-fitshape],[data-pick-setting]').forEach(function (b) {
        b.setAttribute('aria-pressed', 'false');
      });
      var pmin = $('[data-price-min]'), pmax = $('[data-price-max]');
      if (pmin) pmin.value = ''; if (pmax) pmax.value = '';
      var err = $('[data-add-error]'); if (err) err.textContent = '';

      paintSteps(); paintSettings(); paintBar();
      go(1);
    });
  }

  /* ---------- add to cart ----------
     Shopify cannot merge two products into one physical line without an app,
     so the setting and the stone go in as two lines sharing a _ring hidden
     property. The cart template groups them and prices them as one ring. */
  function addRing(btn) {
    if (!state.setting || !state.stone) return;

    // Sample data has no Shopify variant ids. Posting the row id would 404 or,
    // worse, add someone else's product — fail loudly instead.
    var note = $('[data-add-error]');

    /* No size, no order. Everything else here can be changed afterwards; a ring
       made to the wrong finger cannot. */
    if (!sizeChosen()) {
      if (note) note.textContent = 'Choose a finger size first \u2014 or pick "Not sure" and we will make it to a standard size.';
      var sizeSel = $('[data-size]');
      if (sizeSel) sizeSel.focus();
      return;
    }

    /* THE METAL CHOSEN ON THE SETTING PAGE DECIDES WHICH VARIANT IS BOUGHT.
       Posting the row's default variant charged the 14k price for an 18k or
       platinum ring — the review said $839.99 and the cart would have taken
       $529.99. */
    var metal = currentMetal(state.setting);
    var settingVariant = metal ? metal.variant_id : state.setting.variant_id;

    if (!settingVariant || !state.stone.variant_id) {
      if (note) {
        note.textContent = 'This is sample data — connect real Shopify products ' +
          'to enable checkout.';
      }
      return;
    }

    var ringId = 'R' + Date.now().toString(36).toUpperCase();
    /* Carries what the workshop makes to AND what the customer picked, so
       "US 7 (UK N½)" leaves nothing to interpret on the bench. */
    var common = { _ring: ringId, 'Ring size': sizeForCart() };

    var payload = {
      items: [
        { id: settingVariant, quantity: 1,
          properties: Object.assign({}, common, {
            'Set with': state.stone.carat.toFixed(2) + 'ct ' + state.stone.shape +
              ' · ' + state.stone.color + ' / ' + state.stone.clarity
          }) },
        { id: state.stone.variant_id, quantity: 1,
          properties: Object.assign({}, common, {
            'Certification status': state.stone.lab && state.stone.cert ? state.stone.lab + ' ' + state.stone.cert : 'Non-certified',
            /* Same reason: the cart must not name a metal the customer did
               not choose. */
            'Set in': state.setting.style + ' \u00b7 ' + (metal ? metal.name : state.setting.metal)
          }) }
      ]
    };

    var certRequest = $('[data-certification-request]');
    if (!(state.stone.lab && state.stone.cert) && certRequest && certRequest.checked) {
      payload.items[1].properties['Certification request'] = '$25 certification service selected; allow at least 8–10 days';
    }

    if (note) note.textContent = '';
    btn.disabled = true;
    var original = btn.textContent;
    btn.textContent = 'Adding…';

    window.ClavisCertification.prepare(payload.items, state.stone, !(state.stone.lab && state.stone.cert) && certRequest && certRequest.checked).then(function(items) {
      return fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({items:items}) });
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'Could not add'); });
      window.location.href = '/cart';
    }).catch(function (err) {
      btn.disabled = false;
      btn.textContent = original;
      if (note) note.textContent = err.message;
    });
  }

  /* ---------- boot ---------- */
  function boot(settingsData, stonesData) {
    var F = settingsData.fields;
    settings = settingsData.rows.map(function (r) {
      var o = {}; for (var i = 0; i < F.length; i++) o[F[i]] = r[i]; return o;
    });

    var prev = restore();
    state.size = prev.size;
    state.shape = prev.shape;
    state.metal = prev.metal;
    state.region = prev.region;
    if (prev.setting) {
      state.setting = settings.filter(function (s) { return s.id === prev.setting; })[0] || null;
    }
    if (prev.stone && stonesData) {
      var SF = stonesData.fields;
      var iId = SF.indexOf('id');
      var iNo = SF.indexOf('stone_no');
      var found = stonesData.rows.filter(function (r) {
        return String(r[iId]) === prev.stone || (iNo > -1 && String(r[iNo]) === prev.stone);
      })[0];
      if (found) {
        var o = {}; for (var i = 0; i < SF.length; i++) o[SF[i]] = found[i];
        state.stone = o;
      }
    }

    /* Chips first: bind() delegates, but syncChips and paintSettings both read
       elements that only exist once the index has been turned into chips. */
    buildFilterChips();
    buildGridShapes();
    initLayout();
    bind();
    bindSource();
    syncChips();
    paintSteps();
    paintSettings();
    paintBar();
    var sz = $('[data-size]'); if (sz) sz.value = state.size;
    go(state.setting && state.stone ? 3 : state.setting ? 2 : 1);
  }

  var sUrl = root.getAttribute('data-settings-index');
  var dUrl = root.getAttribute('data-diamond-index');

  if (window.__RS_SETTINGS__) {
    boot(window.__RS_SETTINGS__, window.__DS_INDEX__ || null);
  } else {
    Promise.all([
      fetch(sUrl).then(function (r) { return r.json(); }),
      dUrl ? fetch(dUrl).then(function (r) { return r.json(); }).catch(function () { return null; })
           : Promise.resolve(null)
    ]).then(function (r) { boot(r[0], r[1]); })
      .catch(function (err) {
        $('[data-settings]').innerHTML =
          '<div class="rs-empty" style="grid-column:1/-1"><h3>Settings could not be loaded</h3>' +
          '<p class="muted">' + esc(err.message) + '</p></div>';
      });
  }
})();
